/**
 * Reflow a workspace split-tree for a laptop / Retina canvas.
 *
 * Ultrawide profiles keep many siblings in one horizontal row (1×6 charts).
 * That is the right idea of WHAT is on the page — every leaf stays — but on
 * a ~1300 CSS-px MacBook those panes become unreadably thin. This wraps any
 * horizontal run wider than `cols` into a vertical stack of shorter rows so
 * the page can scroll instead of compressing.
 *
 * Pure. Leaf ids / components / aliases are preserved. New split ids are
 * derived from the parent so a re-run is stable.
 */
import { treeComponents, type WsNode } from "./workspacePages";

export const LAPTOP_CHART_COLS = 2;
export const LAPTOP_TILE_COLS = 3;

/** Small mosaic tiles — more per row, shorter bands. */
export function isTileComponent(id: string): boolean {
  return (
    id.startsWith("lb:") ||
    id.startsWith("ratio:") ||
    id.startsWith("bucket:") ||
    id.startsWith("fred:") ||
    id.startsWith("mb2:chart")
  );
}

/** Full chart desks — two per row, tall bands. */
export function isChartComponent(id: string): boolean {
  if (id.startsWith("lb:")) return false;
  return /priceChart|advancedChart|tvAdvanced|multiChart|^chart$|fastChart|kline/i.test(id);
}

export function suggestedLaptopCols(root: WsNode): number {
  const comps = treeComponents(root);
  if (comps.length === 0) return LAPTOP_CHART_COLS;
  if (comps.every(isTileComponent)) return LAPTOP_TILE_COLS;
  return LAPTOP_CHART_COLS;
}

function wrapRows(children: WsNode[], cols: number, idHint: string): WsNode {
  const rows: WsNode[] = [];
  for (let i = 0; i < children.length; i += cols) {
    const slice = children.slice(i, i + cols);
    rows.push(
      slice.length === 1
        ? slice[0]!
        : { id: `${idHint}-r${i}`, t: "split", dir: "h", children: slice },
    );
  }
  if (rows.length === 1) return rows[0]!;
  return { id: `${idHint}-stack`, t: "split", dir: "v", children: rows };
}

function reflowNode(node: WsNode, cols: number): WsNode {
  if (node.t === "leaf") return node;
  const children = node.children.map((c) => reflowNode(c, cols));
  if (node.dir === "h" && children.length > cols) {
    return wrapRows(children, cols, node.id);
  }
  return { ...node, children };
}

/** Same leaves, laptop-width rows. Idempotent on an already-reflowed tree. */
export function reflowForLaptop(root: WsNode, cols?: number): WsNode {
  return reflowNode(root, cols ?? suggestedLaptopCols(root));
}

export function flattenComponents(node: WsNode): Array<string | null> {
  if (node.t === "leaf") return [node.component];
  return node.children.flatMap(flattenComponents);
}

/**
 * How many page-tall bands a (already-reflowed) tree occupies. A vertical
 * stack adds; a horizontal run is one band as tall as its tallest child.
 */
export function countScrollBands(node: WsNode): number {
  if (node.t === "leaf") return 1;
  if (node.dir === "v") {
    return node.children.reduce((n, c) => n + countScrollBands(c), 0);
  }
  return Math.max(1, ...node.children.map(countScrollBands));
}

/** RGL rows per scroll band (ROW_H=30 → 660px ≈ a 70vh chart row). */
export const GRID_BAND_ROWS = 22;
/** Floor so a one-band page still fills the laptop viewport. */
export const GRID_MIN_ROWS = 26;

export function suggestedGridRows(root: WsNode | null): number {
  const bands = root ? countScrollBands(reflowForLaptop(root)) : 3;
  return Math.max(GRID_MIN_ROWS, bands * GRID_BAND_ROWS);
}

export type LaptopGridItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
};

export type LaptopGrid = {
  panes: Array<{ i: string; type: string }>;
  layouts: { lg: LaptopGridItem[] };
};

export function isLaptopGrid(v: unknown): v is LaptopGrid {
  if (!v || typeof v !== "object") return false;
  const g = v as LaptopGrid;
  return Array.isArray(g.panes) && !!g.layouts && Array.isArray(g.layouts.lg);
}

/**
 * Reflow a page's react-grid-layout for a laptop: one pane becomes full-width
 * and as tall as its tree's bands; several panes wrap into a 2-column stack.
 * Pane identities are preserved. Idempotent on an already-stacked grid.
 */
export function reflowGridLayout(grid: LaptopGrid, bandRows: number): LaptopGrid {
  const h = Math.max(GRID_MIN_ROWS, bandRows);
  const byI = new Map((grid.layouts.lg ?? []).map((l) => [l.i, l]));
  if (grid.panes.length <= 1) {
    const p = grid.panes[0];
    if (!p) return { panes: grid.panes, layouts: { lg: [] } };
    const prev = byI.get(p.i);
    return {
      panes: grid.panes,
      layouts: {
        lg: [{ i: p.i, x: 0, y: 0, w: 12, h, minW: prev?.minW ?? 4, minH: prev?.minH ?? 8 }],
      },
    };
  }
  const colW = 6;
  const next: LaptopGridItem[] = [];
  let y = 0;
  for (let i = 0; i < grid.panes.length; i++) {
    const p = grid.panes[i]!;
    const prev = byI.get(p.i);
    const col = i % 2;
    if (col === 0 && i > 0) y += h;
    next.push({
      i: p.i,
      x: col * colW,
      y,
      w: colW,
      h,
      minW: prev?.minW ?? 3,
      minH: prev?.minH ?? 8,
    });
  }
  return { panes: grid.panes, layouts: { lg: next } };
}
